import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, Send } from "lucide-react";
import api from "../../api";
import SalesCertProctorHud from "../../components/SalesCertProctorHud";
import { IntegrityBanner, IntegrityWatermark } from "../../components/IntegrityOverlay";
import { nextSalescertSection, isAttemptExhaustedError } from "../../utils/salescertNav";
import { trackClick } from "../../hooks/useActivityLogger";

/** VI Sales Cert · Section 1 — 10 MCQs timed at 20 minutes (~120 sec/question).
 *  2 attempts allowed · best score is captured on the final report.
 *  Auto-submits when timer hits zero. Refreshing mid-attempt forfeits ONLY
 *  that attempt (0 marks) and consumes one of the two slots. */
export default function SalesCertMCQ() {
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "loading" }); // loading|ready|submitting|scored|error
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [current, setCurrent] = useState(0);
  const [attemptInfo, setAttemptInfo] = useState({ attempt_no: 1, attempts_max: 2, previous_best_marks: 0 });
  const timerRef = useRef(null);
  const submittedRef = useRef(false);

  const loadAttempt = () => {
    setState({ phase: "loading" });
    setErr("");
    setAnswers({});
    setCurrent(0);
    submittedRef.current = false;
    api.get("/participant/salescert/mcq")
      .then((r) => {
        setQuestions(r.data.questions);
        setRemaining(r.data.timer_seconds);
        setAttemptInfo({
          attempt_no: r.data.attempt_no || 1,
          attempts_max: r.data.attempts_max || 2,
          previous_best_marks: r.data.previous_best_marks || 0,
        });
        setState({ phase: "ready" });
        trackClick("salescert_mcq_start", { question_count: r.data.count, attempt_no: r.data.attempt_no });
      })
      .catch((e) => {
        // Exhausted attempts / forfeited state → the hub already shows
        // this clearly. Bounce back silently rather than dropping the
        // candidate onto a "Something went wrong" error screen.
        if (isAttemptExhaustedError(e)) {
          nav("/participant/salescert", { replace: true });
          return;
        }
        setErr(e?.response?.data?.detail || String(e));
        setState({ phase: "error" });
      });
  };

  useEffect(() => {
    loadAttempt();
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.phase !== "ready") return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          if (!submittedRef.current) submit(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [state.phase]);

  // Single-attempt integrity guard — warn the candidate before they close /
  // refresh / navigate away while the MCQ timer is running. If they proceed
  // anyway the server auto-forfeits the attempt (0 marks) on next GET.
  useEffect(() => {
    if (state.phase !== "ready") return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [state.phase]);

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setState({ phase: "submitting" });
    trackClick("salescert_mcq_submit", { auto, answered: Object.keys(answers).length, remaining_sec: remaining });
    try {
      const r = await api.post("/participant/salescert/mcq/submit", { answers });
      // Compute next section for the CTA on the scored screen
      const status = await api.get("/participant/salescert/status").then((x) => x.data).catch(() => null);
      const nxt = nextSalescertSection(status, "mcq");
      setResult({ ...r.data, auto_submitted: auto, next: nxt });
      setState({ phase: "scored" });
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
      setState({ phase: "error" });
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const answered = Object.keys(answers).length;

  if (state.phase === "loading") return <div className="p-10 text-sm text-[#878787]">Loading MCQs…</div>;

  if (state.phase === "error") return (
    <div className="max-w-2xl mx-auto p-8 fade-in" data-testid="salescert-mcq-error">
      <AlertTriangle className="text-[#EE2737] mb-3" size={28} />
      <h2 className="font-heading text-2xl font-bold mb-2">Something went wrong</h2>
      <p className="text-sm text-[#525252] mb-6">{err}</p>
      <button onClick={() => nav("/participant/salescert")} className="btn btn-secondary">Back to certification</button>
    </div>
  );

  if (state.phase === "scored") return (
    <div className="max-w-3xl mx-auto p-8 space-y-6 fade-in" data-testid="salescert-mcq-scored">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="text-[#059669]" size={28} />
        <h2 className="font-heading text-3xl font-bold">Attempt submitted</h2>
      </div>
      {result.auto_submitted && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E] text-xs px-4 py-2 uppercase tracking-widest font-bold">
          Auto-submitted at time-up
        </div>
      )}
      <div className="card p-6" data-testid="salescert-mcq-attempt-summary">
        <div className="overline mb-2">
          Attempt {result.attempt_no} of {result.attempts_max} · Recorded
        </div>
        <p className="text-sm text-[#525252]">
          Your response has been recorded. Your consolidated report will be shared by
          the Vodafone Idea L&amp;D team once the assessment window closes — please do
          not expect a score on this screen.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {result.can_retake ? (
          <button
            onClick={loadAttempt}
            className="btn btn-primary"
            data-testid="salescert-mcq-retake"
          >
            Take attempt {result.attempt_no + 1} of {result.attempts_max}
          </button>
        ) : null}
        {result.next && (
          <button
            onClick={() => nav(result.next.href)}
            className={result.can_retake ? "btn btn-secondary" : "btn btn-primary"}
            data-testid="salescert-mcq-next-section"
          >
            Continue to {result.next.label} <ChevronRight size={14} />
          </button>
        )}
        <button
          onClick={() => nav("/participant/salescert")}
          className="btn btn-ghost"
          data-testid="salescert-mcq-back"
        >
          Back to hub
        </button>
      </div>
      {result.can_retake && (
        <div className="text-[11px] text-[#878787] leading-relaxed">
          You have one more attempt available. Only your best score is captured on the
          final report — retaking will never lower it.
        </div>
      )}
    </div>
  );

  const q = questions[current];
  const lowTime = remaining <= 60;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6 fade-in relative" data-testid="salescert-mcq-attempt">
      <SalesCertProctorHud />
      <IntegrityBanner />
      <IntegrityWatermark testid="mcq-watermark" />

      {/* Sticky header — timer stays visible while candidate scrolls to
          answer options and hit Next. */}
      <div
        className="sticky top-0 z-30 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB] flex items-center justify-between"
        data-testid="mcq-sticky-header"
      >
        <div>
          <div className="overline">KNOWLEDGE MCQ · 50 MARKS · ATTEMPT {attemptInfo.attempt_no}/{attemptInfo.attempts_max}</div>
          <div className="text-xs text-[#525252] mt-1">
            Question <span className="font-bold text-[#0A0A0A]" data-testid="salescert-mcq-current">{current + 1}</span> of {questions.length} · Answered {answered}/{questions.length}
          </div>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 font-heading font-black text-xl ${
          lowTime ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#0A0A0A] text-white"
        }`} data-testid="salescert-mcq-timer">
          <Clock size={18} /> {mmss(remaining)}
        </div>
      </div>

      <div className="progress-bar"><div style={{ width: `${((current + 1) / questions.length) * 100}%` }} /></div>

      {/* 2-attempt integrity warning — visible on every question */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#FEF3C7] border-l-4 border-[#F59E0B] text-[11px] text-[#92400E]" data-testid="salescert-mcq-single-attempt-warning">
        <AlertTriangle size={14} className="flex-shrink-0" />
        <span>
          <strong>Do not close or reload this window mid-attempt</strong> — leaving forfeits this attempt
          {attemptInfo.attempt_no < attemptInfo.attempts_max
            ? " (0 marks) and consumes one of your two attempts."
            : " (0 marks). This is your final attempt."}
        </span>
      </div>

      <div className="card p-6" data-testid={`salescert-mcq-question-${q.id}`}>
        <div className="overline mb-3">Q{current + 1} · {(q.competency || "").replace(/_/g, " ")}</div>
        <p className="font-semibold mb-5 text-[15px] leading-relaxed">{q.question}</p>
        <div className="space-y-2">
          {q.options.map((o) => (
            <label
              key={o.key}
              data-testid={`salescert-mcq-${q.id}-${o.key}`}
              className={`flex gap-3 items-start p-4 cursor-pointer border ${
                answers[q.id] === o.key ? "border-[#EE2737] bg-[#EE2737]/5" : "border-[#E5E7EB] hover:border-[#0A0A0A]"
              } transition-all`}
            >
              <input
                type="radio"
                name={q.id}
                className="mt-1 accent-[#EE2737]"
                checked={answers[q.id] === o.key}
                onChange={() => setAnswers({ ...answers, [q.id]: o.key })}
              />
              <span className="text-[14px]">
                <span className="font-heading font-black mr-2">{o.key}.</span>
                {o.text || o.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
        <button
          onClick={() => setCurrent(Math.max(0, current - 1))}
          disabled={current === 0}
          className="btn btn-ghost"
          data-testid="salescert-mcq-prev"
        >
          <ChevronLeft size={16} /> Previous
        </button>
        {current < questions.length - 1 ? (
          <button
            onClick={() => setCurrent(current + 1)}
            className="btn btn-primary"
            data-testid="salescert-mcq-next"
          >
            Next <ChevronRight size={16} />
          </button>
        ) : (
          <button
            onClick={() => submit(false)}
            disabled={state.phase === "submitting"}
            className="btn btn-primary"
            data-testid="salescert-mcq-submit"
          >
            {state.phase === "submitting" ? "Submitting…" : <>Submit <Send size={14} /></>}
          </button>
        )}
      </div>
    </div>
  );
}
